import { eur } from "../../hooks/useVertraege";
import type { kalkVorschau } from "../../lib/wizard";
import { zahl } from "../../lib/zahl";

/** Kleine Bausteine des Vertrags-Wizards. Layout in `styles/wizard.css`. */

export function Row({ l, r, strong }: { l: string; r: string; strong?: boolean }) {
  return (
    <div className={"pf-wz-row" + (strong ? " is-stark" : "")}>
      <span className="pf-wz-row-l">{l}</span>
      <span className="pf-wz-row-r">{r}</span>
    </div>
  );
}

export function MonateFehlen({ n }: { n: number }) {
  return (
    <div className="pf-wz-warn">
      {n === 1 ? "Eine Position hat" : `${n} Positionen haben`} noch keine Monate und{" "}
      {n === 1 ? "zählt" : "zählen"} deshalb nicht mit — Monate im Jahresplan setzen.
    </div>
  );
}

export function Kalkbox({ kalk }: { kalk: ReturnType<typeof kalkVorschau> }) {
  return (
    <div className="pf-card pf-wz-kalk">
      <Row l="Jahresnetto" r={eur(kalk.jahres_netto)} strong />
      <Row l="Monatsrate" r={`${eur(kalk.monats_netto)}/Monat`} />
      <Row l="Geplante Stunden/Jahr" r={`${zahl(kalk.jahres_zeit_stunden, 1)} h`} />
      <Row l="Deckungsbeitrag" r={`${eur(kalk.deckungsbeitrag)}${kalk.db_pro_stunde ? ` (${eur(kalk.db_pro_stunde)}/h)` : ""}`} />
      <Row l="Lohnanteil (§ 35a)" r={eur(kalk.lohnanteil_netto_35a)} />
    </div>
  );
}

/**
 * Privat oder gewerblich — die Frage entscheidet den halben Vertrag.
 *
 * Bewusst NICHT aus `kunde.kundentyp` übernommen: dort stehen 253 von 284
 * Kunden auf „firma", darunter offensichtliche Privatleute. Und derselbe Kunde
 * kann einen privaten und einen gewerblichen Vertrag haben. Gefragt wird nach
 * dem Zweck, nicht nach der Rechtsform — das ist die Frage, die § 13 BGB stellt.
 */
export function VertragsartWahl({ wert, onWahl }: { wert: string; onWahl: (v: string) => void }) {
  return (
    <div className="pf-card pf-wz-oben">
      <div className="pf-wz-titel">Wer schließt diesen Vertrag?</div>
      <div className="pf-wz-klein pf-wz-unten">
        Danach richten sich Laufzeit, Widerrufsrecht und Preisangaben. Im Zweifel Privatperson —
        das ist die sichere Seite.
      </div>
      <div className="pf-wz-wahl">
        {[
          { v: "verbraucher", t: "Privatperson", u: "Verbrauchervertrag" },
          { v: "unternehmer", t: "Firma, Verein oder WEG", u: "gewerblicher Zweck" },
        ].map(o => (
          <button key={o.v} className={"pf-btn pf-wz-wahl-knopf" + (wert === o.v ? " is-on" : "")}
            onClick={() => onWahl(o.v)}>
            <div className="pf-wz-titel">{o.t}</div>
            <div className="pf-wz-wahl-unter">{o.u}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Was vor dem Festschreiben noch fehlt.
 *
 * Rot hält auf — der Server vergibt ohne diese Angaben keine Vertragsnummer.
 * Gelb hält nicht auf, sondern zeigt, was einem Kunden später auffallen wird:
 * eine Menge, die im Garten keine Deckung hat, oder ein erfasster Bestandteil
 * ohne Leistung.
 */
export function Pruefliste({ p }: { p: { ok: boolean; fehler: string[]; warnungen: string[] } }) {
  if (p.ok && !p.warnungen.length) {
    return (
      <div className="pf-card pf-wz-abstand pf-wz-gut">
        <div className="pf-wz-titel">Vertrag ist vollständig</div>
        <div className="pf-wz-klein pf-wz-oben-4">
          Anschriften, Positionen und Mengen passen zusammen.
        </div>
      </div>
    );
  }
  return (
    <div className="pf-card pf-wz-abstand">
      {p.fehler.length > 0 && (
        <>
          <div className="pf-wz-titel pf-wz-rot">Das hält den Vertrag auf</div>
          <ul className="pf-wz-liste is-fehler">
            {p.fehler.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </>
      )}
      {p.warnungen.length > 0 && (
        <>
          <div className="pf-wz-titel">Bitte noch einmal ansehen</div>
          <ul className="pf-wz-liste is-warnung">
            {p.warnungen.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

/** Bruttobetrag des ersten Vertragsjahres — die Schätzung vor der Zahlweise.
 *  Seit 26.10.2026 der Wert der Einsätze ab dem Beginnmonat (`planNetto`), nicht
 *  mehr ein Zwölftelanteil. Die genauen Raten rechnet der Server
 *  (`vertrag_raten.erstes_jahr`); je nach Zahlweise kann die Summe um
 *  Rundungscents abweichen. */
export function erstesJahrBrutto(rumpfNetto: number): number {
  // Wie `vertrag_raten.posten` für Verbraucher: erst der Bruttopreis, dann geteilt.
  return Math.round(rumpfNetto * 1.19 * 100) / 100;
}
